import React, { useEffect, useMemo, useState } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Segment } from '@fxtr/i18n';

import { Table, Row, Col, Input, message } from 'antd';

import { Header, Title } from '$app/shared/components/Header';
import { Container } from '$app/shared/components/Container';
import Loading from '$app/shared/components/Loading';
import { fetchHeroes, getHeroesErrorMessage } from '../../heroesApi';

import './styles.scss';
import InvoicingModal from './components/InvoicingModal';

function HeroesListPage({ history }) {
  const [heroes, setHeroes] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadHeroes = async () => {
      try {
        const nextHeroes = await fetchHeroes();
        if (isMounted) {
          setHeroes(nextHeroes);
        }
      } catch (error) {
        message.error(getHeroesErrorMessage(error));
        if (isMounted) {
          setHeroes([]);
        }
      }
    };

    loadHeroes();

    return () => {
      isMounted = false;
    };
  }, []);

  const columns = [
    {
      key: 'staffNumber',
      title: 'Staff Nr.',
      dataIndex: 'staffNumber',
      width: '10%',
      sorter: (a, b) => a.staffNumber - b.staffNumber,
      render: (value, { segment }) => (segment === Segment.Fixter_FR ? `🇫🇷 ${value}` : `🇬🇧 ${value}`),
    },
    {
      key: 'zoneName',
      title: 'Zone',
      dataIndex: 'zoneName',
      width: '20%',
      sorter: (a, b) => a.zoneName?.localeCompare(b.zoneName),
    },
    {
      key: 'firstName',
      title: 'First Name',
      dataIndex: 'firstName',
      width: '20%',
      sorter: (a, b) => a.firstName?.localeCompare(b.firstName),
    },
    {
      key: 'lastName',
      title: 'Last Name',
      dataIndex: 'lastName',
      width: '20%',
      sorter: (a, b) => a.lastName?.localeCompare(b.lastName),
    },
    {
      key: 'emailAddress',
      title: 'Email',
      dataIndex: 'emailAddress',
      width: '20%',
      sorter: (a, b) => a.emailAddress?.localeCompare(b.emailAddress),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      width: '10%',
      sorter: (a, b) => a.status?.localeCompare(b.status),
    },
  ];

  const filteredHeroes = useMemo(() => {
    if (!heroes) {
      return heroes;
    }

    const normalizedSearch = search.toLowerCase();
    const visibleHeroes = normalizedSearch
      ? heroes.filter((hero) => (
        (hero.zoneName || '').toLowerCase().includes(normalizedSearch)
        || (hero.firstName || '').toLowerCase().includes(normalizedSearch)
        || (hero.lastName || '').toLowerCase().includes(normalizedSearch)
        || (hero.emailAddress || '').toLowerCase().includes(normalizedSearch)
      ))
      : heroes;

    return [...visibleHeroes].sort((a, b) => a.staffNumber - b.staffNumber);
  }, [heroes, search]);

  if (!heroes) {
    return <Loading />;
  }

  return (
    <Container style={{ height: window.innerHeight }}>
      <Header>
        <Row justify="space-around" align="middle">
          <Col span={6}>
            <Title>Heroes</Title>
            <Link to="/heroes/new">Create Hero</Link>
            <InvoicingModal />
          </Col>
          <Col span={18} style={{ textAlign: 'right' }}>
            <Input.Search
              placeholder="Search"
              onChange={(event) => setSearch(event.target.value)}
              style={{ width: 200 }}
              defaultValue=""
            />
          </Col>
        </Row>
      </Header>
      <Table
        size="small"
        rowKey={(record) => record.driverId}
        pagination={false}
        dataSource={filteredHeroes}
        columns={columns}
        onRow={(record) => ({
          onClick: () => history.push(`/heroes/${record.driverId}/edit`),
        })}
      />
    </Container>
  );
}

export default withRouter(HeroesListPage);
